import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Chip, Divider,
  List, ListItem, ListItemIcon, ListItemText, Select, MenuItem, FormControl,
} from '@mui/material';
import {
  AddCircleOutline as AddedIcon,
  RemoveCircleOutline as RemovedIcon,
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import axios from '../api/axios';

const fmtCurrency = (v) =>
  `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const changeColor = (type) => {
  if (type === 'item_added' || type === 'room_added') return '#16a34a';
  if (type === 'item_removed' || type === 'room_removed') return '#ef5350';
  return '#fb8c00';
};

const ChangeIcon = ({ type }) => {
  if (type === 'item_added' || type === 'room_added') {
    return <AddedIcon sx={{ color: '#16a34a' }} />;
  }
  if (type === 'item_removed' || type === 'room_removed') {
    return <RemovedIcon sx={{ color: '#ef5350' }} />;
  }
  return <EditIcon sx={{ color: '#fb8c00' }} />;
};

const ChangeRow = ({ change }) => {
  const color = changeColor(change.type);
  const renderText = () => {
    switch (change.type) {
      case 'item_added':
        return (
          <>
            <strong>{change.item_title}</strong> added to {change.room_name} ({fmtCurrency(change.cost)})
          </>
        );
      case 'item_removed':
        return (
          <>
            <strong>{change.item_title}</strong> removed from {change.room_name} ({fmtCurrency(change.cost)})
          </>
        );
      case 'item_cost_changed': {
        const up = change.delta > 0;
        return (
          <>
            <strong>{change.item_title}</strong> ({change.room_name}) cost
            {' '}
            <Box component="span" sx={{ color: up ? '#ef5350' : '#16a34a', fontWeight: 600 }}>
              {fmtCurrency(change.before)} → {fmtCurrency(change.after)}
            </Box>
            {' '}
            ({up ? '+' : ''}{fmtCurrency(change.delta)})
          </>
        );
      }
      case 'item_title_changed':
        return (
          <>
            ({change.room_name}) renamed <em>"{change.before}"</em> → <strong>"{change.after}"</strong>
          </>
        );
      case 'item_type_changed':
        return (
          <>
            <strong>{change.item_title}</strong> ({change.room_name}) type changed: {change.before} → {change.after}
          </>
        );
      case 'room_added':
        return (
          <>
            Room <strong>{change.room_name}</strong> added with {change.item_count} item{change.item_count === 1 ? '' : 's'} ({fmtCurrency(change.added_cost)})
          </>
        );
      case 'room_removed':
        return (
          <>
            Room <strong>{change.room_name}</strong> removed ({change.item_count} item{change.item_count === 1 ? '' : 's'}, {fmtCurrency(change.removed_cost)})
          </>
        );
      default:
        return change.type;
    }
  };
  return (
    <ListItem sx={{ pl: 0, borderLeft: `3px solid ${color}`, mb: 0.5, backgroundColor: `${color}0A`, borderRadius: 1 }}>
      <ListItemIcon sx={{ minWidth: 36 }}><ChangeIcon type={change.type} /></ListItemIcon>
      <ListItemText primary={<Typography variant="body2">{renderText()}</Typography>} />
    </ListItem>
  );
};

const RevisionDiffDialog = ({ open, onClose, estimateId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromRevision, setFromRevision] = useState('');
  const [toRevision, setToRevision] = useState('');

  const fetchDiff = useCallback(async (fromR, toR) => {
    if (!estimateId) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (fromR) params.from = fromR;
      if (toR) params.to = toR;
      const response = await axios.get(`/api/estimates/${estimateId}/revisions/diff`, { params });
      setData(response.data);
      if (response.data.from) setFromRevision(response.data.from.revision_number);
      if (response.data.to) setToRevision(response.data.to.revision_number);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load revisions.');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => {
    if (open) {
      setData(null);
      fetchDiff();
    }
  }, [open, fetchDiff]);

  const allRevisions = data?.all_revisions || [];
  const hasDiff = data && data.from && data.to;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon /> Revision history
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && data && !hasDiff && (
          <Alert severity="info">{data.message || 'No comparison available yet.'}</Alert>
        )}

        {!loading && hasDiff && (
          <>
            {allRevisions.length > 2 && (
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <Typography variant="body2" color="text.secondary">Compare</Typography>
                <FormControl size="small">
                  <Select
                    value={fromRevision}
                    onChange={(e) => { setFromRevision(e.target.value); fetchDiff(e.target.value, toRevision); }}
                  >
                    {allRevisions.map((r) => (
                      <MenuItem key={r.revision_number} value={r.revision_number}>
                        Rev {r.revision_number} · {fmtCurrency(r.total_cost)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ArrowForwardIcon />
                <FormControl size="small">
                  <Select
                    value={toRevision}
                    onChange={(e) => { setToRevision(e.target.value); fetchDiff(fromRevision, e.target.value); }}
                  >
                    {allRevisions.map((r) => (
                      <MenuItem key={r.revision_number} value={r.revision_number}>
                        Rev {r.revision_number} · {fmtCurrency(r.total_cost)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Chip label={`Revision ${data.from.revision_number}`} variant="outlined" />
              <Box>
                <Typography variant="caption" color="text.secondary">Submitted {fmtDate(data.from.submitted_at)}</Typography>
                <Typography variant="body2">{fmtCurrency(data.from.total_cost)}</Typography>
              </Box>
              <ArrowForwardIcon color="action" />
              <Chip label={`Revision ${data.to.revision_number}`} color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Submitted {fmtDate(data.to.submitted_at)}</Typography>
                <Typography variant="body2">{fmtCurrency(data.to.total_cost)}</Typography>
              </Box>
              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Total change</Typography>
                <Typography
                  variant="h6"
                  sx={{ color: data.total_delta > 0 ? '#ef5350' : data.total_delta < 0 ? '#16a34a' : 'text.primary' }}
                >
                  {data.total_delta > 0 ? <UpIcon sx={{ verticalAlign: 'middle' }} /> : data.total_delta < 0 ? <DownIcon sx={{ verticalAlign: 'middle' }} /> : null}
                  {data.total_delta > 0 ? '+' : ''}{fmtCurrency(data.total_delta)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {data.changes.length === 0 ? (
              <Alert severity="success">
                Nothing changed between these revisions.
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {data.changes.length} change{data.changes.length === 1 ? '' : 's'}
                </Typography>
                <List dense>
                  {data.changes.map((c, idx) => (
                    <ChangeRow key={idx} change={c} />
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionDiffDialog;
